import { ValidationIcon } from "@ui/components/internal/ValidationIcon";
import {
  useCharacterCount,
  useWizardFieldMessage,
  WizardFieldFooter,
  wizardField,
} from "@ui/components/internal/WizardField";
import { resolveValidationState } from "@ui/components/shared/resolveValidationState";
import type { VariantProps } from "@ui/lib/tv";
import { composeRenderProps } from "@ui/primitives/shared";
import {
  TextField,
  TextFieldDescription,
  TextFieldGroup,
  TextFieldInput,
  TextFieldLabel,
  type TextFieldProps,
} from "@ui/primitives/TextField";
import type { ReactNode } from "react";

type WizardTextFieldVariants = VariantProps<typeof wizardField>;

export interface WizardTextFieldProps extends TextFieldProps {
  label: string;
  isLabelHidden?: boolean;
  color?: WizardTextFieldVariants["color"];
  size?: WizardTextFieldVariants["size"];
  placeholder?: string;
  /** Leading icon — use sticker icons (`<Icon name="..." type="sticker" />`) to match the
   *  wizard text field design. */
  leadIcon?: ReactNode;
  /** Inline prefix rendered between the lead icon and the input, styled like muted input text —
   *  e.g. MoneyField's currency symbol. Decorative (`aria-hidden`); convey it in the label. */
  prefix?: ReactNode;
  /** Validation message rendered below the field when `isInvalid`. */
  errorMessage?: ReactNode;
  /** Success message rendered below the field once it has validated clean. Takes a green
   *  ring + check icon. Suppressed while `isInvalid`. Mirrors StandardTextField. */
  successMessage?: ReactNode;
  /** When true, renders a live character count in the footer (right). With `maxLength` it shows
   *  `used/max` and turns pink near the limit; with `recommendedLength` it shows a bare count that
   *  only appears once the value is past the recommendation. */
  showCharacterCount?: boolean;
  /** Soft length recommendation. Past it, the count becomes visible (progressive disclosure) in
   *  muted secondary text and `fitHintMessage` is shown — a gentle "this may get truncated" nudge
   *  that never blocks input. Distinct from the hard `maxLength`. */
  recommendedLength?: number;
  /** Hint shown (left of the footer) once the value exceeds `recommendedLength`. Rendered through
   *  the field's description slot so it is associated with the input for assistive tech. */
  fitHintMessage?: ReactNode;
}

export function WizardTextField({
  label,
  isLabelHidden,
  color = "purple",
  size = "5xl",
  placeholder,
  leadIcon,
  prefix,
  errorMessage,
  successMessage,
  showCharacterCount,
  recommendedLength,
  fitHintMessage,
  ...props
}: WizardTextFieldProps) {
  const count = useCharacterCount({
    value: typeof props.value === "string" ? props.value : "",
    maxLength: props.maxLength,
    recommendedLength,
    isEnabled: showCharacterCount,
  });
  const showFitHint = count.overRecommended && !props.isInvalid && fitHintMessage != null;
  const validationState = resolveValidationState(props.isInvalid, successMessage);
  const { messageId, ariaDescribedBy } = useWizardFieldMessage({
    isInvalid: props.isInvalid,
    errorMessage,
    successMessage,
    ariaDescribedBy: props["aria-describedby"],
  });
  // Count goes pink in step with the ring + error message when over a hard limit or nearing
  // maxLength; the soft over-recommendation nudge leaves it muted (its base `text-secondary`).
  const styles = wizardField({ color, size, validationState, nearLimit: props.isInvalid || count.nearLimit });

  return (
    <TextField
      {...props}
      aria-describedby={ariaDescribedBy}
      className={composeRenderProps(props.className, (className) => styles.root({ className }))}
    >
      {({ isDisabled }) => (
        <>
          <TextFieldLabel className={isLabelHidden ? "sr-only" : undefined}>{label}</TextFieldLabel>
          <TextFieldGroup
            className={({ isFocusWithin }) =>
              wizardField({
                color,
                size,
                isFocused: isFocusWithin && !validationState,
                isDisabled,
                validationState,
              }).field()
            }
          >
            {leadIcon ? (
              <span aria-hidden="true" className={styles.leadIcon()}>
                {leadIcon}
              </span>
            ) : null}
            {prefix != null ? (
              <span aria-hidden="true" className={styles.prefix()}>
                {prefix}
              </span>
            ) : null}
            <TextFieldInput className={styles.input()} placeholder={placeholder} />
            {validationState ? <ValidationIcon className={styles.validationIcon()} state={validationState} /> : null}
          </TextFieldGroup>
          <WizardFieldFooter
            classNames={{ messageSlot: styles.messageSlot(), message: styles.message(), count: styles.count() }}
            countText={count.isVisible ? count.text : undefined}
            errorMessage={errorMessage}
            hint={
              showFitHint ? (
                // Muted helper text (text-secondary, matching Field.Description), rendered through
                // the description slot so RAC links it to the input via aria-describedby — read on
                // focus rather than the count being re-announced on every keystroke.
                <TextFieldDescription className={`${styles.message()} text-secondary`}>
                  {fitHintMessage}
                </TextFieldDescription>
              ) : null
            }
            // The fit hint (description slot) already conveys the length state to AT, so the
            // numeric echo is decorative when it shows — hidden to avoid per-keystroke
            // double-announcing. With no hint (e.g. the maxLength-only tagline) it stays audible.
            isCountHidden={showFitHint}
            isInvalid={props.isInvalid}
            messageId={messageId}
            successMessage={successMessage}
          />
        </>
      )}
    </TextField>
  );
}
